import { Box, Button } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import PdfPreviewModal from "../../../components/student/PdfPreviewModal";
import RegisterMarkSchemeModal from "./models/register-mark-scheme-modal";
import { brandPalette } from "../../../constants/brandPalette";
import { toast } from "react-toastify";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const MARK_SCHEME_PUBLIC_BASE = "https://api.edutopiaonline.com/public/";

const resolveSchemeFileUrl = (scheme) => {
  const path = scheme?.url || "";
  if (!path) return "";
  if (/^https?:\/\//i.test(String(path)) || String(path).startsWith("blob:")) {
    return String(path);
  }
  const clean = String(path).replace(/^\//, "");
  return `${MARK_SCHEME_PUBLIC_BASE}${clean}`;
};

const isRegistered = (scheme) => {
  const id = scheme?.grader_scheme_id;
  return id !== null && id !== undefined && id !== "" && id !== 0 && id !== "0";
};

const MarkSchemesTable = ({ searchTerm = "" }) => {
  const markSchemes = useSelector((state) => state.markSchemes.markSchemes);
  const isLoading = useSelector((state) => state.markSchemes.isLoading);
  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("Mark Scheme");
  const [schemeToRegister, setSchemeToRegister] = useState(null);

  const list = useMemo(() => {
    const raw = Array.isArray(markSchemes) ? markSchemes : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((s) => (s?.title || s?.name || "").toLowerCase().includes(q));
  }, [markSchemes, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const Data = list.map((data) => {
    const registered = isRegistered(data);
    return {
      ...data,
      id: data?.id,
      title: data?.title || data?.name || "—",
      preview: (
        <Button
          size="sm"
          borderRadius="full"
          variant="outline"
          borderColor={C.border}
          color={C.primary}
          fontWeight="700"
          _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
          onClick={(e) => {
            e.stopPropagation();
            const url = resolveSchemeFileUrl(data);
            if (!url) {
              toast.error("No file available to preview");
              return;
            }
            setPreviewTitle(data?.title || data?.name || "Mark Scheme");
            setPreviewUrl(url);
          }}
        >
          Preview
        </Button>
      ),
      register: (
        <Button
          size="sm"
          borderRadius="full"
          bg={registered ? "transparent" : C.primary}
          color={registered ? C.secondary : "white"}
          border="1px solid"
          borderColor={registered ? C.border : C.primary}
          fontWeight="800"
          isDisabled={registered || isLoading}
          _hover={
            registered
              ? {}
              : { bg: C.secondary }
          }
          onClick={(e) => {
            e.stopPropagation();
            if (registered) return;
            if (!data?.title || !data?.url) {
              toast.error("Mark scheme title and URL are required to register");
              return;
            }
            setSchemeToRegister(data);
          }}
        >
          {registered ? "Already registered" : "Register"}
        </Button>
      ),
    };
  });

  const columns = [
    { Header: "Scheme title", accessor: "title" },
    { Header: "Preview", accessor: "preview" },
    { Header: "Register", accessor: "register" },
  ];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <>
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          bg="var(--surface-raised)"
          border="1px solid"
          borderColor={C.border}
          borderRadius="16px"
          overflow="hidden"
          boxShadow="sm"
        >
          <CTable
            selectedData={() => {}}
            Columns={columns}
            Data={Data}
            path="openModel"
            Title=""
            subTitle=""
            btnTitle=""
            placeHolder=""
            noSearchBar
            noFilter
            footerBtnTitle
            filterList
            addButton
            addButtonTitle=""
            setPage={setPage}
            setPerPage={setPerPage}
            currentpage={pageNumber}
            setPageNumber={setPageNumber}
            noSecondSearchBar
            isLoading={isLoading}
            perPage={size}
            totalPage={totalPage}
            wrapperProps={{ maxH: "unset", overflow: "auto" }}
            tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
            theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
          />
        </Box>
      </Box>

      <PdfPreviewModal
        isOpen={Boolean(previewUrl)}
        onClose={() => setPreviewUrl("")}
        pdfUrl={previewUrl}
        title={previewTitle}
      />

      <RegisterMarkSchemeModal
        isOpen={Boolean(schemeToRegister)}
        scheme={schemeToRegister}
        fileUrl={schemeToRegister ? resolveSchemeFileUrl(schemeToRegister) : ""}
        onClose={() => setSchemeToRegister(null)}
      />
    </>
  );
};

export default MarkSchemesTable;
